






























































import { Vue, Component } from 'vue-property-decorator'
import type { ImportacaoDeNota } from '@/models/ImportacaoDeNota'
import { resumoDaTriagem, distribuicaoCompleta, enxutarImportacao } from '@/usecases/importacao-de-notas/importacaoHelpers'
import { FinalizarImportacaoDeNotaDoFornecedorUseCase } from '@/usecases/fornecedor/FinalizarImportacaoDeNotaDoFornecedorUseCase'
import { uploadFilesToS3 } from '@/shareds/s3/files'
import AlertModule from '@/store/vuex/aplicacao/AlertModule'
import EtapaUpload from './etapas/EtapaUpload.vue'
import EtapaConferencia from './etapas/EtapaConferencia.vue'
import EtapaQuantidades from './etapas/EtapaQuantidades.vue'
import EtapaResumo from './etapas/EtapaResumo.vue'

interface FlagsDeFinalizacao {
	naoDeveMovimentarEstoque: boolean
	naoDeveAtualizarTabelaDePreco: boolean
}

/**
 * Nova tela de importação de notas (wizard).
 * Scaffold da Fase 1 — as etapas são preenchidas incrementalmente
 * (ver PROPOSTA: Upload → Conferência → Resumo).
 *
 * Reaproveita o contrato `ImportacaoDeNota` e os endpoints existentes;
 * a tela antiga (`documentos-fiscais-v2`) permanece intacta.
 */
@Component({
	components: { EtapaUpload, EtapaConferencia, EtapaQuantidades, EtapaResumo },
})
export default class TelaDeImportacaoDeNotaNova extends Vue {
	etapaAtual = 1
	importacao: ImportacaoDeNota | null = null
	finalizando = false

	private finalizarUseCase = new FinalizarImportacaoDeNotaDoFornecedorUseCase()

	get podeAvancarDaConferencia(): boolean {
		if (!this.importacao) return false
		return resumoDaTriagem(this.importacao.produtos).pendentes === 0
	}

	get podeAvancarDasQuantidades(): boolean {
		if (!this.importacao) return false
		return this.podeAvancarDaConferencia && distribuicaoCompleta(this.importacao.produtos)
	}

	avancar() {
		if (this.etapaAtual < 4) this.etapaAtual += 1
	}

	voltar() {
		if (this.etapaAtual > 1) this.etapaAtual -= 1
	}

	onNotaCarregada(importacao: ImportacaoDeNota) {
		this.importacao = importacao
	}

	async onFinalizar(flags: FlagsDeFinalizacao) {
		if (!this.importacao) return
		if (this.importacao.produtos.some(p => !p.isProdutoEstaAgrupado && !p.encontrado)) {
			AlertModule.setError('Existem produtos não associados.')
			return
		}

		try {
			this.finalizando = true

			const dados = enxutarImportacao({
				...this.importacao,
				naoDeveMovimentarEstoque: flags.naoDeveMovimentarEstoque,
				naoDeveAtualizarTabelaDePreco: flags.naoDeveAtualizarTabelaDePreco,
			})

			const blob = new Blob([JSON.stringify(dados)], { type: 'application/json' })
			const arquivo = new File([blob], 'importacaoDeNota.json', { type: 'application/json' })

			const infos = await uploadFilesToS3([arquivo], 'importacao-de-notas')
			const url = infos.map(({ config }) => config.url)[0]
			if (!url) {
				AlertModule.setError('Ocorreu um erro ao subir o arquivo. Contate o suporte.')
				return
			}

			const resultado = await this.finalizarUseCase.execute(
				url,
				flags.naoDeveMovimentarEstoque,
				flags.naoDeveAtualizarTabelaDePreco,
			)

			if (!resultado.feedbackDosTitulos) {
				AlertModule.setSuccess('Importação iniciada com sucesso! Acompanhe em Processos.')
			} else {
				AlertModule.setInfo(`Importação iniciada, porém ${resultado.feedbackDosTitulos}`)
			}

			this.importacao = null
			this.etapaAtual = 1
		} catch (error: any) {
			AlertModule.setError(error)
		} finally {
			this.finalizando = false
		}
	}
}
